import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Application } from '../api/client';
import { SkeletonBar, SkeletonBlock } from './Skeleton';
import AppIconImage from './AppIconImage';
import PageBackButton from './PageBackButton';

const APP_TYPE_KEYS: Record<string, string> = {
  web_app: 'panel.webApp',
  web_api: 'panel.webApi',
  mobil_app: 'panel.mobileApp',
};

export interface AppPageLayoutProps {
  appId?: string;
  app: Application | null;
  loading?: boolean;
  error?: string | null;
  /** Sayfa başlığı: "Detay", "Appscreen", "App Information" vb. */
  pageTitle?: string;
  /** Overrides auto-derived parent path for the back button. */
  backTo?: string;
  children: ReactNode;
}

/** Uygulama alt sayfaları için ortak şablon: app header (isim, tip • açıklama) + children. */
const AppPageLayout: React.FC<AppPageLayoutProps> = ({
  app,
  loading = false,
  error = null,
  pageTitle,
  backTo,
  children,
}) => {
  const { t } = useTranslation();
  if (loading) {
    return (
      <div className="p-4 space-y-4">
        <PageBackButton to={backTo} />
        <div className="min-w-0">
          <SkeletonBar width="40%" className="h-7 mb-2" />
          <SkeletonBar width="60%" className="h-4" />
        </div>
        <SkeletonBlock />
        <SkeletonBlock />
      </div>
    );
  }

  if (error || !app) {
    return (
      <div className="p-4 space-y-4">
        <PageBackButton to={backTo} />
        {children}
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <PageBackButton to={backTo} />
      <div className="min-w-0">
        <div className="flex items-center gap-3 flex-wrap">
          {app.iconPath || app.iconUpdatedAt ? (
            <AppIconImage
              appId={app.id}
              iconPath={app.iconPath}
              iconUpdatedAt={app.iconUpdatedAt}
              className="w-12 h-12 rounded-xl object-cover border border-gray-200 dark:border-gray-600 shrink-0"
              alt={`${app.name} icon`}
            />
          ) : null}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white truncate">
                {app.name}
              </h1>
              {pageTitle && (
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  · {pageTitle}
                </span>
              )}
            </div>
            <p className="text-gray-600 dark:text-gray-400 text-sm mt-0.5">
              {(APP_TYPE_KEYS[app.type] ? t(APP_TYPE_KEYS[app.type]) : app.type)} • {app.description || '—'}
            </p>
          </div>
        </div>
      </div>
      {children}
    </div>
  );
};

export default AppPageLayout;
